import { useMutation } from '@tanstack/react-query';

import { PREFIX_URL } from '@/src/api/config';
import { UpdateEmailFileEntryResponse } from '@/src/components/project/Inbox2/api/updateEmailFileStatus';
import { EmailDeliveryType } from '@/src/components/project/Inbox2/types';
import { axios } from '@/src/lib/axios/axios';
import { MutationConfig, queryClient } from '@/src/lib/react-query/react-query';
import { generateRoutePath } from '@/src/routes';

import { emailFileActivityLogsKeys } from './getEmailFileActivityLogs';
import { emailFileDetailsKeys } from './getEmailFileDetails';

import { emailEntriesKeys } from '../../../api/getEmailEntries';
import { emailThreadAttachmentsKeys } from '../../../api/getEmailThreadAttachments';

// const url = `${PREFIX_URL}/${BASE_PROJECT_PATH}/inbox/read`;
const url = `${PREFIX_URL}/inbox-threads/:inboxEntryId/emails/:emailId/attachments/:fileId`;

export type MarkReadDTO = {
  projectId: string;
  inboxEntryId: number;
  emailId: number;
  fileId: number;
  read: boolean;
};

export const markRead = ({
  projectId,
  inboxEntryId,
  emailId,
  fileId,
  read,
}: MarkReadDTO): Promise<UpdateEmailFileEntryResponse> => {
  return axios.put(generateRoutePath(url, { projectId, inboxEntryId, emailId, fileId }), { read });
};

type MarkReadOptions = {
  projectId: string;
  inboxEntryId: number;
  emailId: number;
  fileId: number;
  config?: MutationConfig<typeof markRead>;
};

export const useMarkReadEmailFile = ({
  projectId,
  inboxEntryId,
  emailId,
  fileId,
  config,
}: MarkReadOptions) => {
  return useMutation({
    onSuccess: (data) => {
      queryClient.setQueryData(
        [...emailFileDetailsKeys.detail({ projectId, emailId, inboxEntryId, fileId })],
        (prev: any) => {
          return {
            ...prev,
            data: data.data['inbox-email-attachment'],
          };
        }
      );

      // Sync with email entries list
      if (queryClient.getQueriesData([...emailEntriesKeys.lists({ projectId, inboxEntryId })])) {
        queryClient.setQueriesData(
          [...emailEntriesKeys.lists({ projectId, inboxEntryId })],
          (prev: any) => {
            return {
              ...prev,
              pages: prev.pages.map((page) => {
                return {
                  ...page,
                  data: {
                    ...page.data,
                    emails: page.data.emails.map((emailEntry) => {
                      if (emailEntry.id === emailId) {
                        return data.data['inbox-email'];
                      }
                      return emailEntry;
                    }),
                  },
                };
              }),
            };
          }
        );
      }

      // Sync with email thread attachments list
      if (
        queryClient.getQueriesData([
          ...emailThreadAttachmentsKeys.lists({
            inboxEntryId,
            projectId,
            type: EmailDeliveryType.inbound,
          }),
        ])
      ) {
        queryClient.setQueriesData(
          [
            ...emailThreadAttachmentsKeys.lists({
              inboxEntryId,
              projectId,
              type: EmailDeliveryType.inbound,
            }),
          ],
          (prev: any) => {
            return {
              ...prev,
              pages: prev.pages.map((page) => {
                return {
                  ...page,
                  data: {
                    ...page.data,
                    attachments: page.data.attachments.map((attachmentItem) => {
                      if (attachmentItem.id === fileId) {
                        return {
                          ...attachmentItem,
                          read: data.data['inbox-email-attachment'].read,
                        };
                      }
                      return attachmentItem;
                    }),
                  },
                };
              }),
            };
          }
        );
      }

      if (
        queryClient.getQueriesData([
          ...emailThreadAttachmentsKeys.lists({
            inboxEntryId,
            projectId,
            type: EmailDeliveryType.outbound,
          }),
        ])
      ) {
        queryClient.setQueriesData(
          [
            ...emailThreadAttachmentsKeys.lists({
              inboxEntryId,
              projectId,
              type: EmailDeliveryType.outbound,
            }),
          ],
          (prev: any) => {
            return {
              ...prev,
              pages: prev.pages.map((page) => {
                return {
                  ...page,
                  data: {
                    ...page.data,
                    attachments: page.data.attachments.map((attachmentItem) => {
                      if (attachmentItem.id === fileId) {
                        return {
                          ...attachmentItem,
                          read: data.data['inbox-email-attachment'].read,
                        };
                      }
                      return attachmentItem;
                    }),
                  },
                };
              }),
            };
          }
        );
      }

      queryClient.invalidateQueries([
        ...emailFileActivityLogsKeys.detail({ projectId, fileId, emailId, inboxEntryId }),
      ]);
    },
    ...config,
    mutationFn: ({ read }) => markRead({ inboxEntryId, emailId, projectId, fileId, read }),
  });
};
